/**
 * /night-vision — the camera: open it, read what it can do in the dark, and
 * ask it for more light where the browser lets a page ask.
 *
 * The door is CameraManager (public/js/camera-manager.js): the getUserMedia
 * watchdog, the relax ladder, the cause of a refusal and its sentence. Zoom,
 * torch, exposure and frame rate go through window.__trackConstraint — one
 * call in flight per track, the newest value wins, a muted track is waited for.
 *
 * Light the sensor itself can gather is worth more than any gain applied after
 * it: a longer exposure collects more photons, a gain only scales the noise
 * with them. So, where the camera reports the controls (Chrome on Android and
 * on desktop webcams; Safari reports none of them):
 *   • «more light»  — exposure compensation to its top and the frame rate
 *     capped, so the camera's own auto exposure may hold each frame longer
 *     (exposure can never be longer than one frame);
 *   • manual        — exposure time and ISO set by hand.
 * ⚠ Chrome on Android keeps a manual exposure on the CAMERA after the page
 * closes: the next site opens a camera stuck at our settings. close() puts the
 * exposure back to continuous before letting go.
 */

export class NightCamera {
    constructor(opts) {
        this.video = opts.video;
        this.mgr = new window.CameraManager({ slug: opts.slug || 'night-vision' });
        this.stream = null;
        this.track = null;
        this.caps = {};
        this.settings = {};
        this.touched = false;   // we changed exposure/frame rate — undo on close
    }

    isSupported() { return this.mgr.isSupported(); }

    /**
     * @param {{deviceId?:string, facingMode?:string, height:number, button?:Element}} o
     */
    async open(o) {
        const h = o.height || 720;
        const video = { width: { ideal: Math.round(h * 16 / 9) }, height: { ideal: h }, frameRate: { ideal: 30 } };
        if (o.deviceId) video.deviceId = { exact: o.deviceId };
        else if (o.facingMode) video.facingMode = { ideal: o.facingMode };
        await this.close();
        const stream = await this.mgr.startRaw({ video, audio: false }, o.button ? { button: o.button } : undefined);
        this.stream = stream;
        this.track = stream.getVideoTracks()[0] || null;
        // Frames, not play(): a camera that never sends one never settles play().
        if (!(await this.mgr.show(this.video, stream))) {
            await this.close();
            const none = new Error('the camera sends no frames');
            none.code = 'unresponsive';
            throw none;
        }
        this.refresh();
        return stream;
    }

    refresh() {
        const t = this.track;
        this.caps = {}; this.settings = {};
        if (!t) return;
        try { this.caps = t.getCapabilities ? (t.getCapabilities() || {}) : {}; } catch (_) { this.caps = {}; }
        try { this.settings = t.getSettings ? (t.getSettings() || {}) : {}; } catch (_) { this.settings = {}; }
    }

    async close() {
        const t = this.track;
        if (t && this.touched && t.readyState === 'live') {
            // give the camera back the way we found it (see the header)
            const back = {};
            if (Array.isArray(this.caps.exposureMode) && this.caps.exposureMode.indexOf('continuous') >= 0) back.exposureMode = 'continuous';
            if (this.caps.exposureCompensation) back.exposureCompensation = 0;
            if (Object.keys(back).length) { try { await withTimeout(window.__trackConstraint(t, back, { waitMs: 800 }), 1000); } catch (_) { } }
        }
        this.touched = false;
        try { this.mgr.stop(); } catch (_) { }
        if (this.stream) { try { this.stream.getTracks().forEach(tr => tr.stop()); } catch (_) { } }
        this.stream = null; this.track = null; this.caps = {}; this.settings = {};
        try { this.video.srcObject = null; } catch (_) { }
    }

    get live() { return !!(this.track && this.track.readyState === 'live' && this.video.videoWidth > 0); }
    get width() { return this.video.videoWidth; }
    get height() { return this.video.videoHeight; }
    get facing() { return this.settings.facingMode || ''; }
    get label() { return (this.track && this.track.label) || ''; }

    async devices() { try { return await this.mgr.enumerate(); } catch (_) { return []; } }

    /* ── what the camera offers ── */

    /** {min,max,step} or null */
    range(key) {
        const c = this.caps[key];
        if (!c || typeof c !== 'object' || !(c.max > c.min)) return null;
        return { min: c.min, max: c.max, step: c.step || 0 };
    }
    hasTorch() { return !!this.caps.torch; }
    zoomRange() { return this.range('zoom'); }
    canManualExposure() {
        return Array.isArray(this.caps.exposureMode) && this.caps.exposureMode.indexOf('manual') >= 0 && !!this.range('exposureTime');
    }
    /** The census the page shows («what your camera lets the page do»). */
    census() {
        const r = (k) => this.range(k);
        return {
            zoom: r('zoom'),
            torch: this.hasTorch(),
            exposureTime: this.canManualExposure() ? r('exposureTime') : null,
            iso: r('iso'),
            exposureCompensation: r('exposureCompensation'),
            frameRate: r('frameRate'),
            exposureModes: Array.isArray(this.caps.exposureMode) ? this.caps.exposureMode.slice() : [],
        };
    }

    /* ── asking for light ── */

    async setZoom(z) {
        const r = this.zoomRange();
        if (!r) return { ok: false, reason: 'unsupported' };
        const v = Math.min(r.max, Math.max(r.min, z));
        const res = await window.__trackConstraint(this.track, { zoom: v });
        this.refresh();
        return res;
    }

    async setTorch(on) {
        if (!this.hasTorch()) return { ok: false, reason: 'unsupported' };
        const res = await window.__trackConstraint(this.track, { torch: !!on });
        this.refresh();
        return res;
    }

    /**
     * mode: 'auto' | 'boost' | 'manual'. For manual, `time` is in the
     * camera's own units (100 µs per the spec) and `iso` its own scale.
     * @returns {Promise<{ok:boolean, applied:object}>}
     */
    async setExposure(mode, o) {
        const t = this.track;
        if (!t) return { ok: false, applied: {} };
        const set = {};
        const comp = this.range('exposureCompensation');
        const fr = this.range('frameRate');
        const modes = Array.isArray(this.caps.exposureMode) ? this.caps.exposureMode : [];
        if (mode === 'manual' && this.canManualExposure()) {
            set.exposureMode = 'manual';
            const et = this.range('exposureTime');
            if (o && o.time > 0) set.exposureTime = Math.min(et.max, Math.max(et.min, o.time));
            const iso = this.range('iso');
            if (iso && o && o.iso > 0) set.iso = Math.min(iso.max, Math.max(iso.min, o.iso));
            // an exposure longer than a frame needs fewer frames per second
            if (fr && set.exposureTime) {
                const fps = Math.max(fr.min, Math.min(30, Math.floor(10000 / set.exposureTime)));
                set.frameRate = fps;
            }
        } else {
            if (modes.indexOf('continuous') >= 0) set.exposureMode = 'continuous';
            if (comp) set.exposureCompensation = mode === 'boost' ? comp.max : 0;
            if (fr) set.frameRate = mode === 'boost' ? Math.max(fr.min, Math.min(15, fr.max)) : Math.min(30, fr.max);
        }
        if (!Object.keys(set).length) return { ok: false, applied: {} };
        this.touched = this.touched || mode !== 'auto';
        // manual mode first, then the values: some drivers refuse a time while still in auto
        let res;
        if (set.exposureMode === 'manual') {
            res = await window.__trackConstraint(t, { exposureMode: 'manual' });
            const rest = Object.assign({}, set); delete rest.exposureMode;
            if (res.ok && Object.keys(rest).length) res = await window.__trackConstraint(t, rest);
        } else {
            res = await window.__trackConstraint(t, set);
        }
        this.refresh();
        return { ok: !!res.ok, applied: set, reason: res.reason };
    }

    errorMessage(err) {
        const code = (err && err.code) || window.CameraManager.classify(err);
        try { return this.mgr.getErrorMessage(code); } catch (_) { return ''; }
    }
    hasHelp() { try { return this.mgr.hasHelp(); } catch (_) { return false; } }
}

/** A device label that names an infrared camera (Chrome adds «infrared»; Windows Hello devices say «IR»). */
export function isInfraredLabel(label) {
    // «ИК» between non-letters, not \bИК\b: JavaScript's \b knows only Latin letters, it never matched
    return /infra-?red|\bIR\b|(?:^|[^\p{L}\p{N}_])ИК(?![\p{L}\p{N}_])/iu.test(String(label || ''));
}

export function withTimeout(p, ms) {
    return new Promise((resolve, reject) => {
        let done = false;
        const t = setTimeout(() => { if (!done) { done = true; const e = new Error('timeout'); e.name = 'TimeoutError'; reject(e); } }, ms);
        Promise.resolve(p).then((v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } },
            (e) => { if (!done) { done = true; clearTimeout(t); reject(e); } });
    });
}
